/**
 * Island loader (UNIT-04). Scans the page for `[data-island="name"]` elements and
 * lazy-imports the matching esbuild chunk from `/islands/<name>.js`, then calls its
 * exported `mount(el)`. Runs under strict CSP (self-hosted module, no inline script).
 *
 * Island contract: each `packages/client/src/islands/<name>.ts` exports
 * `export function mount(el: HTMLElement): void`.
 *
 * HTMX swaps replace DOM subtrees WITHOUT re-running the initial scan, so islands that arrive
 * via a swap (e.g. the pipeline stepper polls/resumes and carries the equity + correlation
 * chart islands) would never mount and render as empty boxes. We therefore scan on the initial
 * load AND on every `htmx:load`, guarding each element with `data-island-mounted` so a re-scan
 * (or overlapping swaps) never mounts the same island twice.
 */
interface IslandModule {
  mount(el: HTMLElement): void;
}

/** The minimum an element must expose to be claimed — lets the claim step be tested without a DOM. */
export interface ClaimableIsland {
  dataset: { island?: string | undefined; islandMounted?: string | undefined };
}

/** A chunk name must be a bare slug: it is interpolated straight into an import path. */
const SAFE_NAME = /^[a-z0-9-]+$/;

/**
 * Claim every unmounted island in `els`, SYNCHRONOUSLY. Returns the ones this caller now owns.
 *
 * Synchronous and all-at-once is the whole point (UNIT-119). The previous loop flagged each
 * element only when it reached it, with an `await import(...)` in between, and leaned on
 * `querySelectorAll`'s `:not([data-island-mounted])` — which is evaluated ONCE, at query time.
 * That left two holes, and the bug needed both: a scan starting mid-loop saw elements 2..N still
 * unflagged, and each scan's own stale snapshot re-mounted elements the other had since claimed.
 * Two scans DO overlap on an ordinary page load — `start()` scans, then htmx fires `htmx:load` on
 * the body during its own init — so every chart on the results page rendered twice.
 */
export function claimIslands<T extends ClaimableIsland>(els: Iterable<T>): { el: T; name: string }[] {
  const claimed: { el: T; name: string }[] = [];
  for (const el of els) {
    if (el.dataset.islandMounted) continue;
    const name = el.dataset.island;
    if (!name || !SAFE_NAME.test(name)) continue;
    el.dataset.islandMounted = "1";
    claimed.push({ el, name });
  }
  return claimed;
}

async function mountIn(root: ParentNode): Promise<void> {
  const claimed = claimIslands(
    root.querySelectorAll<HTMLElement>("[data-island]:not([data-island-mounted])"),
  );
  for (const { el, name } of claimed) {
    try {
      // Runtime-computed specifier: esbuild leaves this as a real dynamic import.
      const mod = (await import(`/islands/${name}.js`)) as Partial<IslandModule>;
      mod.mount?.(el);
    } catch (err) {
      delete el.dataset.islandMounted; // allow a retry on the next scan
      console.error(`[islands] failed to load "${name}"`, err);
    }
  }
}

function start(): void {
  void mountIn(document);
  // Mount islands inside HTMX-swapped content too. `htmx:load` fires on each newly-loaded node.
  document.body.addEventListener("htmx:load", (e) => {
    const el = e.target;
    if (el instanceof HTMLElement) {
      if (el.matches("[data-island]:not([data-island-mounted])")) void mountIn(el.parentElement ?? document);
      else void mountIn(el);
    }
  });
}

// Guarded so the module can be IMPORTED without a DOM — the claim step above has tests, and this
// workspace has no jsdom. In the browser the guard is always true, so behaviour is unchanged.
if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
}
